import { Maximize2, RotateCcw, TrendingUp, Users, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useIsMobile } from "@/lib/hooks/use-mobile";
import { cn } from "@/lib/utils";

import type {
  DiscountCohortMatricesShape,
  DiscountCohortMatrixShape,
} from "../../types/discount-analytics-types";

interface Props {
  matrices: DiscountCohortMatricesShape;
}

const MAX_MONTH_INDEX = 12;

function cellColor(rate: number): string {
  if (rate <= 0) return "bg-slate-100 text-slate-700 font-semibold";
  if (rate < 0.15) return "bg-rose-100 text-rose-900 font-semibold";
  if (rate < 0.3) return "bg-amber-100 text-amber-900 font-semibold";
  if (rate < 0.5) return "bg-amber-200 text-amber-950 font-semibold";
  if (rate < 0.7) return "bg-emerald-200 text-emerald-950 font-semibold";
  return "bg-emerald-400 text-emerald-950 font-bold";
}

function formatPercent(v: number): string {
  return `${(v * 100).toFixed(0)}%`;
}

function formatMonth(yyyymm: string): string {
  // "2026-05" → "May 2026"
  const [year, month] = yyyymm.split("-");
  if (!year || !month) return yyyymm;
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

function formatRevenue(amountCents: number): string {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(
    Math.round(amountCents / 100)
  );
}

function CohortMatrixBody({
  matrix,
  maxMonthIndex,
  t,
}: {
  matrix: DiscountCohortMatrixShape;
  maxMonthIndex: number;
  t: ReturnType<typeof useTranslation>["t"];
}) {
  return (
    <table className="min-w-full text-sm border-separate border-spacing-0">
      <thead>
        <tr>
          <th className="sticky left-0 z-10 bg-rose-100 border-b-2 border-r border-rose-300 px-3 py-2.5 text-center text-xs font-bold uppercase tracking-wide text-rose-900">
            {t("discounts.cohort.cohortHeader")}
          </th>
          <th className="bg-rose-100 border-b-2 border-rose-300 px-3 py-2.5 text-center text-xs font-bold uppercase tracking-wide text-rose-900">
            {t("discounts.cohort.sizeHeader")}
          </th>
          {Array.from({ length: maxMonthIndex }).map((_, idx) => (
            <th
              key={idx}
              className="bg-rose-100 border-b-2 border-rose-300 px-2 py-2.5 text-center text-xs font-bold text-rose-900 whitespace-nowrap"
            >
              {idx === 0
                ? t("discounts.cohort.monthHeaderFirst")
                : t("discounts.cohort.monthHeaderAfter", { count: idx })}
            </th>
          ))}
          <th className="bg-emerald-100 border-b-2 border-l border-emerald-300 px-3 py-2.5 text-center text-xs font-bold uppercase tracking-wide text-emerald-900 whitespace-nowrap">
            {t("discounts.cohort.totalRevenueHeader")}
          </th>
        </tr>
      </thead>
      <tbody>
        {matrix.rows.map((row) => {
          const totalRevenue = row.cells.reduce((sum, c) => sum + c.revenueGenerated, 0);
          return (
            <tr key={row.cohortStart}>
              <td className="sticky left-0 z-10 bg-white border-b-2 border-r border-slate-200 px-3 py-2 text-center font-bold text-slate-900 whitespace-nowrap">
                {formatMonth(row.cohortStart)}
              </td>
              <td className="bg-white border-b-2 border-slate-200 px-3 py-2 text-center tabular-nums font-semibold text-slate-900">
                {row.cohortSize}
              </td>
              {Array.from({ length: maxMonthIndex }).map((_, idx) => {
                const cell = row.cells.find((c) => c.monthIndex === idx);
                const rate = cell?.retentionRate ?? 0;
                const active = cell?.activeCount ?? 0;
                const revenue = cell?.revenueGenerated ?? 0;
                return (
                  <TooltipProvider key={idx} delayDuration={100}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <td
                          className={cn(
                            "border-b-2 border-slate-200 px-2 py-2 text-center tabular-nums cursor-default",
                            cellColor(rate)
                          )}
                        >
                          {formatPercent(rate)}
                        </td>
                      </TooltipTrigger>
                      <TooltipContent className="text-xs">
                        <div>
                          <strong>
                            {formatMonth(row.cohortStart)} ·{" "}
                            {idx === 0
                              ? t("discounts.cohort.monthHeaderFirst")
                              : t("discounts.cohort.monthHeaderAfter", { count: idx })}
                          </strong>
                        </div>
                        <div>
                          {t("discounts.cohort.tooltipActive", {
                            active,
                            total: row.cohortSize,
                          })}
                        </div>
                        <div>
                          {t("discounts.cohort.tooltipRevenue", {
                            revenue: (revenue / 100).toFixed(0),
                          })}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              })}
              <td className="bg-emerald-50 border-b-2 border-l border-emerald-200 px-3 py-2 text-center tabular-nums font-bold text-emerald-800 whitespace-nowrap">
                €{formatRevenue(totalRevenue)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function CohortMatrixTable({
  matrix,
  title,
  description,
  emptyState,
}: {
  matrix: DiscountCohortMatrixShape;
  title: string;
  description: string;
  emptyState: string;
}) {
  const { t } = useTranslation("reports");
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (matrix.rows.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4 text-slate-500" />
            {title}
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground italic">{emptyState}</p>
        </CardContent>
      </Card>
    );
  }

  const maxMonthIndex = Math.min(
    MAX_MONTH_INDEX,
    Math.max(...matrix.rows.flatMap((r) => r.cells.map((c) => c.monthIndex + 1)))
  );

  // Mobile: render blurred preview + tap-to-view CTA; open fullscreen
  // landscape view on tap so the wide table has the long axis to breathe.
  if (isMobile) {
    return (
      <>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4 text-slate-500" />
              {title}
            </CardTitle>
            <CardDescription>{description}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative overflow-hidden rounded-xl border border-slate-200 shadow-sm h-56">
              <div className="pointer-events-none select-none blur-[3px] opacity-60 h-full overflow-hidden">
                <CohortMatrixBody matrix={matrix} maxMonthIndex={maxMonthIndex} t={t} />
              </div>
              <div className="absolute inset-0 flex items-center justify-center bg-white/40 backdrop-blur-[1px]">
                <Button
                  type="button"
                  onClick={() => setIsOpen(true)}
                  size="lg"
                  className="shadow-lg flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white font-semibold"
                >
                  <Maximize2 className="h-4 w-4" />
                  {t("discounts.detail.tapToView")}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {isOpen ? (
          <FullscreenLandscapeCohort
            matrix={matrix}
            maxMonthIndex={maxMonthIndex}
            title={title}
            t={t}
            onClose={() => setIsOpen(false)}
          />
        ) : null}
      </>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Users className="h-4 w-4 text-slate-500" />
          {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <CohortMatrixBody matrix={matrix} maxMonthIndex={maxMonthIndex} t={t} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function FullscreenLandscapeCohort({
  matrix,
  maxMonthIndex,
  title,
  t,
  onClose,
}: {
  matrix: DiscountCohortMatrixShape;
  maxMonthIndex: number;
  title: string;
  t: ReturnType<typeof useTranslation>["t"];
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/95 backdrop-blur-sm">
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 z-10 rounded-full bg-white/95 p-2 shadow-lg hover:bg-white"
        aria-label={t("discounts.detail.closeChart")}
      >
        <X className="h-5 w-5 text-slate-900" />
      </button>

      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: "100vh",
          height: "100vw",
          transform: "translate(-50%, -50%) rotate(90deg)",
          transformOrigin: "center",
        }}
      >
        <div className="h-full w-full bg-white px-6 py-4 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-slate-900">{title}</h2>
            <span className="text-xs font-medium text-slate-500 flex items-center gap-1">
              <RotateCcw className="h-3 w-3" />
              {t("discounts.detail.landscapeHint")}
            </span>
          </div>
          <div className="flex-1 min-h-0 overflow-auto">
            <CohortMatrixBody matrix={matrix} maxMonthIndex={maxMonthIndex} t={t} />
          </div>
        </div>
      </div>
    </div>
  );
}

export function DiscountCohortMatrix({ matrices }: Props) {
  const { t } = useTranslation("reports");

  const totalCustomers =
    matrices.discount.rows.reduce((s, r) => s + r.cohortSize, 0) +
    matrices.nonDiscount.rows.reduce((s, r) => s + r.cohortSize, 0);

  if (totalCustomers === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 via-white to-emerald-50 p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-sm">
            <TrendingUp className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-900">
              {t("discounts.cohort.headerTitle")}
            </h3>
            <p className="text-xs text-slate-600">{t("discounts.cohort.headerDescription")}</p>
          </div>
        </div>
      </div>

      <CohortMatrixTable
        matrix={matrices.discount}
        title={t("discounts.cohort.discountTitle")}
        description={t("discounts.cohort.discountDescription")}
        emptyState={t("discounts.cohort.discountEmpty")}
      />
      <CohortMatrixTable
        matrix={matrices.nonDiscount}
        title={t("discounts.cohort.nonDiscountTitle")}
        description={t("discounts.cohort.nonDiscountDescription")}
        emptyState={t("discounts.cohort.nonDiscountEmpty")}
      />
    </div>
  );
}
